import { create } from 'zustand/react';
import { SalonWithAccessResponse, SalonWorkingHoursResponse, UpdateSalonWorkingHoursRequest, UpdateSalonRequest, SalonDetailResponse } from '@api/generated';
import { salonsApi } from '@api/api';

interface WorkingHoursData {
  [key: string]: {
    closed: boolean;
    intervals: Array<{ start: string; end: string; isActive: boolean }>;
  };
}

type SalonState = {
  salons: SalonWithAccessResponse[];
  selectedSalon: SalonWithAccessResponse | null;
  salonDetail: SalonDetailResponse | null;
  workingHours: SalonWorkingHoursResponse[];
  loading: boolean;
  error: string | null;

  // Actions
  loadSalons: () => Promise<void>;
  loadSalonDetail: (salonId: string) => Promise<void>;
  loadWorkingHours: (salonId: string) => Promise<void>;
  updateWorkingHours: (salonId: string, workingHoursData: WorkingHoursData) => Promise<void>;
  updateSalon: (salonId: string, salonData: UpdateSalonRequest) => Promise<void>;
  selectSalon: (salonId: string) => void;
  clearSelectedSalon: () => void;
  clearError: () => void;
};

export const useSalonStore = create<SalonState>((set, get) => ({
  salons: [],
  selectedSalon: null,
  salonDetail: null,
  workingHours: [],
  loading: false,
  error: null,

  loadSalons: async () => {
    set({ loading: true, error: null });

    try {
      const response = await salonsApi.getMySalons();
      set({ salons: response.data, loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při načítání salonů',
        loading: false,
      });
    }
  },

  loadSalonDetail: async (salonId: string) => {
    set({ loading: true, error: null });

    try {
      const response = await salonsApi.getSalonById({ id: salonId });
      set({ salonDetail: response.data, loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při načítání detailu salonu',
        loading: false,
      });
    }
  },

  loadWorkingHours: async (salonId: string) => {
    try {
      const response = await salonsApi.getSalonWorkingHours({ id: salonId });
      set({ workingHours: response.data });
    } catch (error) {
      console.error('Chyba při načítání otevírací doby:', error);
      set({ workingHours: [] });
    }
  },

  updateWorkingHours: async (salonId: string, workingHoursData: WorkingHoursData) => {
    set({ loading: true, error: null });
    
    try {
      // Transform data to API format
      const workingHours = Object.entries(workingHoursData).map(([dayId, dayData]) => ({
        weekday: parseInt(dayId),
        intervals: dayData.intervals.map(interval => ({
          startTime: interval.start + ':00',
          endTime: interval.end + ':00',
          isActive: interval.isActive
        })),
        isActive: !dayData.closed
      }));

      const request: UpdateSalonWorkingHoursRequest = { workingHours };
      await salonsApi.updateSalonWorkingHours({ id: salonId, updateSalonWorkingHoursRequest: request });
      
      // Reload working hours after successful update
      await get().loadWorkingHours(salonId);
      set({ loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při ukládání otevírací doby',
        loading: false,
      });
      throw error;
    }
  },

  updateSalon: async (salonId: string, salonData: UpdateSalonRequest) => {
    set({ loading: true, error: null });
    
    try {
      await salonsApi.updateSalon({ id: salonId, updateSalonRequest: salonData });
      
      // Reload salons after successful update
      await get().loadSalons();
      set({ loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při ukládání salonu',
        loading: false,
      });
      throw error;
    }
  },

  selectSalon: (salonId: string) => {
    const { salons } = get();
    const salon = salons.find(s => s.id === salonId);
    set({ selectedSalon: salon || null });
  },

  clearSelectedSalon: () => {
    set({ selectedSalon: null });
  },

  clearError: () => {
    set({ error: null });
  },
}));